import { useCallback, useMemo, useState } from "react";
import { z } from "zod";
import {
  CAMPAIGN_TABLE_COLUMNS,
  type CampaignTableColumnId,
  type CampaignTableColumnWidths,
  clampCampaignTableColumnWidth,
  getCampaignTableDefaultWidths,
  getCampaignTableGridTemplate,
  getCampaignTableWidth,
} from "@/components/campaign/campaign-table-layout";

const STORAGE_KEY = "adpilot:campaign-table-column-widths:v1";
const storedWidthsSchema = z.record(z.string(), z.number().finite());

function readStoredWidths(): CampaignTableColumnWidths {
  const defaults = getCampaignTableDefaultWidths();
  if (typeof window === "undefined") return defaults;

  try {
    const parsed = storedWidthsSchema.safeParse(
      JSON.parse(window.localStorage.getItem(STORAGE_KEY) ?? "{}"),
    );
    if (!parsed.success) return defaults;

    for (const column of CAMPAIGN_TABLE_COLUMNS) {
      const storedWidth = parsed.data[column.id];
      if (storedWidth != null) {
        defaults[column.id] = clampCampaignTableColumnWidth(column.id, storedWidth);
      }
    }
  } catch {
    return defaults;
  }

  return defaults;
}

function persistWidths(widths: CampaignTableColumnWidths) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(widths));
  } catch {
    // Column resizing still works for this session when browser storage is unavailable.
  }
}

export function useCampaignTableColumnWidths(columnIds: readonly CampaignTableColumnId[]) {
  const [widths, setWidths] = useState<CampaignTableColumnWidths>(readStoredWidths);

  const resizeColumn = useCallback((columnId: CampaignTableColumnId, width: number) => {
    setWidths((current) => {
      const next = {
        ...current,
        [columnId]: clampCampaignTableColumnWidth(columnId, width),
      };
      persistWidths(next);
      return next;
    });
  }, []);

  const resetColumn = useCallback((columnId: CampaignTableColumnId) => {
    const defaultWidth = getCampaignTableDefaultWidths()[columnId];
    setWidths((current) => {
      const next = { ...current, [columnId]: defaultWidth };
      persistWidths(next);
      return next;
    });
  }, []);

  return useMemo(
    () => ({
      widths,
      gridTemplateColumns: getCampaignTableGridTemplate(widths, columnIds),
      tableWidth: getCampaignTableWidth(widths, columnIds),
      resizeColumn,
      resetColumn,
    }),
    [columnIds, resetColumn, resizeColumn, widths],
  );
}
